import { useDispatch, useSelector } from 'react-redux';
import { initialState, setUser, setAuthority } from 'store/auth/userSlice';
import { apiSignIn, apiSignUp, apiReportLoginError } from 'services/AuthService';
import { onSignInSuccess, onSignOutSuccess, setToken } from 'store/auth/sessionSlice';
import appConfig from 'configs/app.config';
import { REDIRECT_URL_KEY } from 'constants/app.constant';
import { useNavigate } from 'react-router-dom';
import useQuery from './useQuery';
import { decodeJwt } from 'jose';

function useAuth() {
    const dispatch = useDispatch();

    const navigate = useNavigate();

    const query = useQuery();

    const { signedIn, token } = useSelector(state => state.auth.session);

    const signIn = async values => {
        const flowId = Array.from(crypto.getRandomValues(new Uint8Array(32)), value => value.toString(16).padStart(2, '0')).join('');
        let stage = 'authentication';
        try {
            const resp = await apiSignIn(values, flowId);
            if (resp.data) {
                stage = 'browser_session';
                const { username } = decodeJwt(resp.data.accessToken);
                dispatch(
                    setUser({
                        userName: resp.data.access.name,
                        email: username,
                    }),
                );
                dispatch(onSignInSuccess());
                if (resp.data) {
                    dispatch(setAuthority(resp.data));
                    dispatch(setToken(resp.data.accessToken));
                }

                const redirectUrl = query.get(REDIRECT_URL_KEY);
                navigate(redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath);
                return {
                    status: 'success',
                    message: '',
                };
            }
        } catch (errors) {
            try {
                await apiReportLoginError({
                    email: values.username, flowId, stage,
                    message: String(errors?.response?.data?.message || errors.message || errors).slice(0, 2000),
                    name: errors.name || 'ClientLoginError',
                    stack: errors.stack?.slice(0, 10000),
                });
            } catch (reportError) {
                console.error('Could not report Portal login error', reportError);
            }
            return {
                status: 'failed',
                message: errors?.response?.data?.message || errors.toString(),
            };
        }
    };

    const signUp = async values => {
        try {
            const resp = await apiSignUp(values);
            if (resp.data) {
                dispatch(onSignInSuccess());
                if (resp.data.user) {
                    dispatch(
                        setUser(
                            resp.data.user || {
                                avatar: '',
                                userName: 'Anonymous',
                                authority: ['USER'],
                                email: '',
                            },
                        ),
                    );
                }
                const redirectUrl = query.get(REDIRECT_URL_KEY);
                navigate(redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath);
                return {
                    status: 'success',
                    message: '',
                };
            }
        } catch (errors) {
            return {
                status: 'failed',
                message: errors?.response?.data?.message || errors.toString(),
                errors: errors?.response?.data?.errors,
            };
        }
    };

    const handleSignOut = () => {
        dispatch(onSignOutSuccess());
        dispatch(setUser(initialState));
        navigate(appConfig.unAuthenticatedEntryPath);
    };

    const signOut = async () => {
        // await apiSignOut();
        handleSignOut();
    };

    return {
        authenticated: token ? signedIn : false,
        signIn,
        signUp,
        signOut,
    };
}

export default useAuth;
